import React, { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/StatusBadge";
import { ImageUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Users, Building2, Loader2, Pencil, Mail } from "lucide-react";
import { toast } from "sonner";

const emptyClient = { company_name: "", contact_name: "", contact_email: "", notes: "", thumbnail_path: null };

const AdminSettings = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyClient);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [inviting, setInviting] = useState(false);
  const [clientUsers, setClientUsers] = useState([]);
  const [cuEmail, setCuEmail] = useState("");

  const load = useCallback(async () => {
    const res = await Promise.all([api.get("/clients"), api.get("/users")]);
    setClients(res[0].data);
    setTeam(res[1].data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditingId(null); setForm(emptyClient); setClientUsers([]); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ company_name: c.company_name, contact_name: c.contact_name, contact_email: c.contact_email, notes: c.notes || "", thumbnail_path: c.thumbnail_path || null });
    setOpen(true);
    api.get(`/clients/${c.id}/users`).then((r) => setClientUsers(r.data)).catch(() => setClientUsers([]));
  };

  const addClientUser = async () => {
    if (!cuEmail || !editingId) { toast.error("Enter an email"); return; }
    try { await api.post(`/clients/${editingId}/users`, { email: cuEmail }); setCuEmail("");
      const r = await api.get(`/clients/${editingId}/users`); setClientUsers(r.data); toast.success("Client user added");
    } catch (e) { toast.error("Could not add user"); }
  };
  const removeClientUser = async (uid) => {
    await api.delete(`/clients/${editingId}/users/${uid}`);
    const r = await api.get(`/clients/${editingId}/users`); setClientUsers(r.data); toast.success("Removed");
  };

  const saveClient = async () => {
    if (!form.company_name || !form.contact_email) { toast.error("Company & email required"); return; }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/clients/${editingId}`, form); toast.success("Client updated"); }
      else { await api.post("/clients", form); toast.success("Client added"); }
      setOpen(false); setForm(emptyClient); setEditingId(null); load();
    } catch (e) { toast.error("Could not save client"); }
    setSaving(false);
  };
  const delClient = async (id) => { await api.delete(`/clients/${id}`); toast.success("Client removed"); load(); };

  const invite = async () => {
    if (!inviteEmail) { toast.error("Enter an email"); return; }
    setInviting(true);
    try {
      await api.post("/users/invite", { email: inviteEmail, role: inviteRole });
      toast.success(`Invited ${inviteEmail} as ${inviteRole}`);
      setInviteEmail(""); load();
    } catch (e) { toast.error("Could not invite"); }
    setInviting(false);
  };
  const changeRole = async (u, role) => {
    await api.put(`/users/${u.user_id}/role`, { role, client_id: role === "client" ? u.client_id : null });
    toast.success("Role updated"); load();
  };
  const delUser = async (id) => { await api.delete(`/users/${id}`); toast.success("Member removed"); load(); };

  return (
    <div className="space-y-10">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Team & Clients</h1>
        <p className="text-zinc-400 mt-1">Invite teammates, manage clients and default thumbnails.</p>
      </div>

      {/* Invite team (admin only) */}
      {isAdmin && (
        <section>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2 mb-4"><Mail className="w-5 h-5 text-brand" /> Invite a team member</h2>
          <div className="bg-ink-800 border border-ink-700 rounded-2xl p-5 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-zinc-300">Email</Label>
              <Input data-testid="invite-email-input" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@email.com" className="bg-ink-900 border-ink-700 mt-1.5" />
            </div>
            <div>
              <Label className="text-zinc-300">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role-select" className="w-[160px] bg-ink-900 border-ink-700 mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-800 border-ink-700 text-white">
                  {["admin", "strategist", "editor"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="invite-submit-button" onClick={invite} disabled={inviting} className="h-11 px-6 rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold">
              {inviting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send invite
            </Button>
          </div>
          <p className="text-zinc-500 text-sm mt-2">They'll get their role automatically the first time they sign in with Google using this email.</p>
        </section>
      )}

      {/* Clients */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Building2 className="w-5 h-5 text-brand" /> Clients</h2>
          <Button data-testid="add-client-button" onClick={openAdd} className="rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold"><Plus className="w-4 h-4 mr-1" /> Add client</Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {clients.map((c) => (
            <div key={c.id} data-testid={`client-card-${c.id}`} className="bg-ink-800 border border-ink-700 rounded-2xl overflow-hidden">
              {c.thumbnail_path && <img src={fileUrl(c.thumbnail_path)} alt={c.company_name} className="w-full h-28 object-cover" />}
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div><h3 className="font-semibold text-lg">{c.company_name}</h3><p className="text-zinc-400 text-sm">{c.contact_name}</p><p className="text-zinc-500 text-sm">{c.contact_email}</p></div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="text-zinc-500 hover:text-brand"><Pencil className="w-4 h-4" /></button>
                    {isAdmin && <button data-testid={`delete-client-${c.id}`} onClick={() => delClient(c.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
                {c.notes && <p className="text-zinc-400 text-sm mt-3">{c.notes}</p>}
              </div>
            </div>
          ))}
          {clients.length === 0 && <p className="text-zinc-500">No clients yet.</p>}
        </div>
      </section>

      {/* Client add/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ink-800 border-ink-700 text-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div><Label className="text-zinc-300">Company name</Label><Input data-testid="client-company-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-ink-900 border-ink-700 mt-1.5" /></div>
            <div><Label className="text-zinc-300">Contact name</Label><Input data-testid="client-contact-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-ink-900 border-ink-700 mt-1.5" /></div>
            <div><Label className="text-zinc-300">Contact email</Label><Input data-testid="client-email-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-ink-900 border-ink-700 mt-1.5" placeholder="Used for their portal login" /></div>
            <div><Label className="text-zinc-300">Notes</Label><Textarea data-testid="client-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-ink-900 border-ink-700 mt-1.5" /></div>
            <ImageUpload label="Default client thumbnail (used as batch cover)" value={form.thumbnail_path} onChange={(p) => setForm({ ...form, thumbnail_path: p })} testid="client-thumb" />
            {editingId && (
              <div className="border-t border-ink-700 pt-4">
                <Label className="text-zinc-300">Client users (all see this client's data only)</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input data-testid="client-user-email-input" value={cuEmail} onChange={(e) => setCuEmail(e.target.value)} placeholder="person@client.com" className="bg-ink-900 border-ink-700" />
                  <Button type="button" data-testid="add-client-user-button" onClick={addClientUser} className="rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold shrink-0">Add</Button>
                </div>
                <div className="space-y-1.5 mt-3">
                  {clientUsers.map((u) => (
                    <div key={u.user_id} data-testid={`client-user-${u.user_id}`} className="flex items-center justify-between bg-ink-900 border border-ink-700 rounded-lg px-3 py-2 text-sm">
                      <span className="truncate">{u.email} <span className="text-zinc-500 text-xs">({u.status === "invited" ? "Invited" : "Active"})</span></span>
                      <button type="button" data-testid={`remove-client-user-${u.user_id}`} onClick={() => removeClientUser(u.user_id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {clientUsers.length === 0 && <p className="text-zinc-500 text-sm">No client users yet.</p>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button data-testid="client-submit-button" onClick={saveClient} disabled={saving} className="rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {editingId ? "Save changes" : "Add client"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team */}
      <section>
        <h2 className="font-display text-2xl font-bold flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-brand" /> Team members</h2>
        <div className="bg-ink-800 border border-ink-700 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-zinc-400 border-b border-ink-700"><tr>{["Name", "Email", "Role", "Status", ""].map((h) => <th key={h} className="text-left font-medium px-5 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {team.map((u) => (
                  <tr key={u.user_id} data-testid={`team-row-${u.user_id}`} className="border-b border-ink-700/60">
                    <td className="px-5 py-3 font-semibold">{u.name}</td>
                    <td className="px-5 py-3 text-zinc-400">{u.email}</td>
                    <td className="px-5 py-3">
                      {isAdmin ? (
                        <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                          <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-[140px] bg-ink-900 border-ink-700 h-9"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-ink-800 border-ink-700 text-white">
                            {["admin", "strategist", "editor"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : <RoleBadge role={u.role} />}
                    </td>
                    <td className="px-5 py-3">
                      <span data-testid={`status-${u.user_id}`} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${u.status === "invited" ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-brand/10 text-brand border-brand/40"}`}>
                        {u.status === "invited" ? "Invited" : "Active"}
                      </span>
                    </td>
                    <td className="px-5 py-3">{isAdmin && u.user_id !== user.user_id && <button data-testid={`delete-user-${u.user_id}`} onClick={() => delUser(u.user_id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
                {team.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-500">No team members yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminSettings;
